import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSession } from '../auth/session.ts';
import { SOUND_EVENTS, SOUND_GROUPS, SOUND_EVENT_BY_ID, type SoundEventDef } from '../audio/events.ts';
import { creditLine, loadLibrary, soundUrl, type LibrarySound, type SoundLibrary } from '../audio/library.ts';
import {
  copyProfile,
  defaultAssignment,
  deleteProfile,
  DEFAULT_PROFILE_ID,
  isMusicEvent,
  listProfiles,
  onSelectedProfile,
  profileOwner,
  resolveAssignment,
  saveProfile,
  selectedProfileId,
  selectProfile,
  type SoundAssignment,
  type SoundProfile,
} from '../audio/profiles.ts';
import { useEditor } from './store.ts';
import { Icon } from './widgets.tsx';

// ---------- preview: one sound at a time ----------

let previewEl: HTMLAudioElement | null = null;
let previewKey: string | null = null;
const previewListeners = new Set<() => void>();
const emitPreview = () => previewListeners.forEach((fn) => fn());

function stopPreview() {
  if (previewEl) {
    previewEl.pause();
    previewEl.removeAttribute('src');
    previewEl.load();
  }
  previewEl = null;
  previewKey = null;
  emitPreview();
}

/** Plays `sound` at the event's volume and pitch, or stops it when it is already playing. */
function togglePreview(lib: SoundLibrary, sound: LibrarySound, key: string, volume: number, pitch: number) {
  if (previewKey === key) {
    stopPreview();
    return;
  }
  stopPreview();
  const el = new Audio(soundUrl(lib, sound));
  el.volume = Math.min(1, Math.max(0, volume));
  el.playbackRate = pitch;
  (el as HTMLAudioElement & { preservesPitch?: boolean }).preservesPitch = false;
  el.onended = () => {
    if (previewEl === el) stopPreview();
  };
  previewEl = el;
  previewKey = key;
  emitPreview();
  void el.play().catch(() => {
    if (previewEl === el) stopPreview();
  });
}

function usePreviewKey(): string | null {
  const [key, setKey] = useState(previewKey);
  useEffect(() => {
    const fn = () => setKey(previewKey);
    previewListeners.add(fn);
    return () => {
      previewListeners.delete(fn);
    };
  }, []);
  return key;
}

function PreviewButton({ lib, sound, k, volume, pitch }: { lib: SoundLibrary; sound: LibrarySound | null; k: string; volume: number; pitch: number }) {
  const playing = usePreviewKey() === k;
  return (
    <button
      className={`icon-btn sound-play ${playing ? 'on' : ''}`}
      title={sound ? (playing ? 'Stop' : 'Listen') : 'Silent'}
      disabled={!sound}
      onClick={(e) => {
        e.stopPropagation();
        if (sound) togglePreview(lib, sound, k, volume, pitch);
      }}
    >
      <Icon name={playing ? 'stop' : 'play'} size={16} />
    </button>
  );
}

// ---------- helpers ----------

const pct = (v: number) => `${Math.round(v * 100)}%`;
const secs = (d: number) => (d >= 10 ? `${Math.round(d)} s` : `${d.toFixed(1)} s`);
/** "sfx_movement_jump9_landing" -> "Movement jump9 landing" */
function displayName(x: LibrarySound): string {
  const n = x.name.replace(/^(sfx|snd|se)_(sounds?_)?/i, '').replace(/_+/g, ' ').replace(/\s+/g, ' ').trim();
  return n ? n[0].toUpperCase() + n.slice(1) : x.name;
}
const sameAssignment = (a: SoundAssignment, b: SoundAssignment) => a.sound === b.sound && a.volume === b.volume && a.pitch === b.pitch;

function SoundName({ sound }: { sound: LibrarySound | null }) {
  if (!sound) return <span className="muted">Silent</span>;
  return (
    <span className="sound-name">
      <span title={sound.name}>{displayName(sound)}</span>
      {sound.original && <span className="sound-badge original">Original</span>}
    </span>
  );
}

// ---------- the dialog ----------

export function SoundProfilesDialog() {
  const s = useEditor();
  const session = useSession();
  const me = session?.id ?? null;
  const [lib, setLib] = useState<SoundLibrary | null>(null);
  const [profiles, setProfiles] = useState<SoundProfile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState(selectedProfileId());
  const [openId, setOpenId] = useState(selected);
  const [draft, setDraft] = useState<SoundProfile | null>(null);
  const [saveState, setSaveState] = useState<'saved' | 'pending' | 'saving' | 'error'>('saved');
  const [tab, setTab] = useState<'sounds' | 'credits'>('sounds');
  const [picking, setPicking] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSave = useRef<SoundProfile | null>(null);

  const close = () => s.set({ modal: null });

  useEffect(() => {
    loadLibrary().then(setLib, () => setError('The sound library could not be loaded.'));
    listProfiles().then(setProfiles, () => setError('Sound profiles need the server, and it cannot be reached right now.'));
    const off = onSelectedProfile(() => setSelected(selectedProfileId()));
    return () => {
      off();
      stopPreview();
      void flushSave();
    };
  }, []);

  const open = profiles?.find((p) => p.id === openId) ?? profiles?.find((p) => p.id === DEFAULT_PROFILE_ID) ?? null;
  const mine = !!open && open.id !== DEFAULT_PROFILE_ID && open.ownerId === me;
  const shown = mine && draft?.id === open.id ? draft : open;

  useEffect(() => {
    setDraft(mine && open ? open : null);
    setPicking(null);
  }, [open?.id, mine]);

  function flushSave(): Promise<void> {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = null;
    const p = pendingSave.current;
    pendingSave.current = null;
    if (!p) return Promise.resolve();
    setSaveState('saving');
    return saveProfile(p).then(
      (saved) => {
        setProfiles((list) => list?.map((x) => (x.id === saved.id ? saved : x)) ?? list);
        if (!pendingSave.current) setSaveState('saved');
      },
      () => setSaveState('error'),
    );
  }

  const edit = (fn: (p: SoundProfile) => SoundProfile) => {
    if (!draft) return;
    const next = fn(draft);
    setDraft(next);
    if (!next.name.trim()) return;
    pendingSave.current = next;
    setSaveState('pending');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flushSave, 600);
  };

  const setEvent = (event: string, a: SoundAssignment | null) =>
    edit((p) => {
      const events = { ...p.events };
      if (!a || (lib && sameAssignment(a, defaultAssignment(lib, event)))) delete events[event];
      else events[event] = a;
      return { ...p, events };
    });

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      s.notify((e as Error).message || 'That did not work');
    } finally {
      setBusy(false);
    }
  };

  const copy = (p: SoundProfile) =>
    run(async () => {
      const name = prompt('Name for your copy', p.id === DEFAULT_PROFILE_ID ? 'My sounds' : `${p.name} (copy)`);
      if (name === null) return;
      await flushSave();
      const made = await copyProfile(p.id, name.trim() || undefined);
      setProfiles((list) => [...(list ?? []), made]);
      setOpenId(made.id);
      selectProfile(made.id);
      s.notify(`Made “${made.name}”. It is now the profile you play with.`);
    });

  const remove = (p: SoundProfile) =>
    run(async () => {
      if (!confirm(`Delete the sound profile “${p.name}”? Anyone using it goes back to Default.`)) return;
      if (pendingSave.current?.id === p.id) pendingSave.current = null;
      await deleteProfile(p.id);
      setProfiles((list) => list?.filter((x) => x.id !== p.id) ?? list);
      if (openId === p.id) setOpenId(DEFAULT_PROFILE_ID);
    });

  const pickingDef = picking ? SOUND_EVENT_BY_ID.get(picking) ?? null : null;

  return (
    <div className="modal-back" onClick={close}>
      <div className="modal sounds" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2>Sound profiles</h2>
          <div className="sound-tabs">
            <button className={`btn small-btn ${tab === 'sounds' ? 'active' : ''}`} onClick={() => setTab('sounds')}><Icon name="sound" size={14} /> Sounds</button>
            <button className={`btn small-btn ${tab === 'credits' ? 'active' : ''}`} onClick={() => setTab('credits')}><Icon name="star" size={14} /> Credits</button>
          </div>
          <button className="icon-btn" onClick={close}><Icon name="close" /></button>
        </div>
        {error && <div className="modal-body muted">{error}</div>}
        {!error && (!lib || !profiles) && <div className="modal-body muted">Loading sounds…</div>}
        {!error && lib && profiles && tab === 'credits' && <Credits lib={lib} />}
        {!error && lib && profiles && tab === 'sounds' && (
          <div className="sounds-layout">
            <div className="sound-profile-list">
              <p className="muted small">Everyone sees every profile. You can change the ones you made, and copy any of them.</p>
              {profiles.map((p) => {
                const own = p.id !== DEFAULT_PROFILE_ID && p.ownerId === me;
                return (
                  <div key={p.id} className={`sound-profile-card ${p.id === open?.id ? 'open' : ''}`} onClick={() => setOpenId(p.id)}>
                    <div className="sound-profile-info">
                      <b title={p.name}>{p.name}</b>
                      <span className="muted small">{own ? 'Yours' : profileOwner(p)}</span>
                    </div>
                    {p.id === selected ? (
                      <span className="sound-badge using" title="This device plays with this profile">In use</span>
                    ) : (
                      <button
                        className="btn small-btn"
                        title="Play with this profile on this device"
                        onClick={(e) => {
                          e.stopPropagation();
                          selectProfile(p.id);
                        }}
                      >
                        Use
                      </button>
                    )}
                    <button
                      className="icon-btn"
                      title="Copy"
                      disabled={busy}
                      onClick={(e) => {
                        e.stopPropagation();
                        void copy(p);
                      }}
                    >
                      <Icon name="copy" size={16} />
                    </button>
                    {own && (
                      <button
                        className="icon-btn"
                        title="Delete"
                        disabled={busy}
                        onClick={(e) => {
                          e.stopPropagation();
                          void remove(p);
                        }}
                      >
                        <Icon name="trash" size={16} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {shown && (
              <ProfileEditor
                lib={lib}
                profile={shown}
                editable={mine}
                saveState={saveState}
                onRename={(name) => edit((p) => ({ ...p, name }))}
                onVolume={(k, v) => edit((p) => ({ ...p, [k]: v }))}
                onEvent={setEvent}
                onPick={setPicking}
                onCopy={() => void copy(shown)}
              />
            )}
          </div>
        )}
        {lib && shown && pickingDef && (
          <SoundPicker
            lib={lib}
            event={pickingDef}
            current={resolveAssignment(lib, shown, pickingDef.id)}
            master={isMusicEvent(pickingDef.id) ? shown.musicVolume : shown.sfxVolume}
            onPick={(sound) => {
              const cur = resolveAssignment(lib, shown, pickingDef.id);
              setEvent(pickingDef.id, { ...cur, sound });
              setPicking(null);
            }}
            onClose={() => setPicking(null)}
          />
        )}
      </div>
    </div>
  );
}

// ---------- one profile ----------

function ProfileEditor(props: {
  lib: SoundLibrary;
  profile: SoundProfile;
  editable: boolean;
  saveState: 'saved' | 'pending' | 'saving' | 'error';
  onRename: (name: string) => void;
  onVolume: (k: 'sfxVolume' | 'musicVolume', v: number) => void;
  onEvent: (event: string, a: SoundAssignment | null) => void;
  onPick: (event: string) => void;
  onCopy: () => void;
}) {
  const { lib, profile, editable } = props;
  const [filter, setFilter] = useState('');
  const q = filter.trim().toLowerCase();
  const changed = Object.keys(profile.events).length;
  return (
    <div className="sound-editor">
      <div className="sound-editor-head">
        {editable ? (
          <input className="text" value={profile.name} maxLength={60} onChange={(e) => props.onRename(e.target.value)} onBlur={(e) => !e.target.value.trim() && props.onRename('Untitled sounds')} />
        ) : (
          <h3 className="sound-editor-title">{profile.name}</h3>
        )}
        {editable ? (
          <span className={`muted small save-${props.saveState}`}>{{ saved: 'Saved', pending: 'Editing…', saving: 'Saving…', error: 'Not saved: the server could not be reached' }[props.saveState]}</span>
        ) : (
          <button className="btn small-btn primary" onClick={props.onCopy}><Icon name="copy" size={14} /> Copy to edit</button>
        )}
      </div>
      {!editable && (
        <p className="muted small">
          {profile.id === DEFAULT_PROFILE_ID ? 'The built-in profile. It keeps the game’s original sounds where it had them.' : `Made by ${profileOwner(profile)}. Only they can change it.`} Copy it to make your own.
        </p>
      )}
      {editable && <p className="muted small">{changed ? `${changed} of ${SOUND_EVENTS.length} sounds changed from Default.` : 'Same as Default so far.'} Changes save by themselves.</p>}
      <div className="sound-masters">
        {(['sfxVolume', 'musicVolume'] as const).map((k) => (
          <label key={k} className="sound-master">
            <span>{k === 'sfxVolume' ? 'Sound effects' : 'Music'}</span>
            <input type="range" min={0} max={1} step={0.05} value={profile[k]} disabled={!editable} onChange={(e) => props.onVolume(k, Number(e.target.value))} />
            <span className="density-value">{pct(profile[k])}</span>
          </label>
        ))}
      </div>
      <input className="text search" placeholder="Find a game action, e.g. “jump” or “door”" value={filter} onChange={(e) => setFilter(e.target.value)} />
      {SOUND_GROUPS.map((group) => {
        const events = SOUND_EVENTS.filter((e) => e.group === group && (!q || `${e.label} ${e.hint} ${e.id}`.toLowerCase().includes(q)));
        if (!events.length) return null;
        return (
          <div key={group} className="sound-group">
            <h3>{group}</h3>
            {events.map((e) => (
              <EventRow key={e.id} lib={lib} profile={profile} event={e} editable={editable} onEvent={props.onEvent} onPick={props.onPick} />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function EventRow({ lib, profile, event, editable, onEvent, onPick }: { lib: SoundLibrary; profile: SoundProfile; event: SoundEventDef; editable: boolean; onEvent: (event: string, a: SoundAssignment | null) => void; onPick: (event: string) => void }) {
  const a = resolveAssignment(lib, profile, event.id);
  const sound = a.sound ? lib.byId.get(a.sound) ?? null : null;
  const master = event.kind === 'music' ? profile.musicVolume : profile.sfxVolume;
  const custom = !!profile.events[event.id];
  return (
    <div className="sound-event">
      <div className="sound-event-main">
        <PreviewButton lib={lib} sound={sound} k={`event:${event.id}`} volume={a.volume * master} pitch={a.pitch} />
        <div className="sound-event-text">
          <div className="sound-event-label">
            <b>{event.label}</b>
            {event.kind === 'loop' && <span className="sound-badge">Loop</span>}
            {custom && <span className="sound-badge changed">Changed</span>}
          </div>
          <span className="muted small">{event.hint}</span>
        </div>
        <button className="sound-current" disabled={!editable} title={editable ? 'Pick a sound' : sound ? creditLine(lib.credits[sound.credit]) : 'Silent'} onClick={() => onPick(event.id)}>
          <SoundName sound={sound} />
          {editable && <Icon name="down" size={14} />}
        </button>
      </div>
      {editable && (
        <div className="sound-event-knobs">
          <label title="Volume of this sound">
            <span className="muted small">Volume</span>
            <input type="range" min={0} max={2} step={0.05} value={a.volume} onChange={(e) => onEvent(event.id, { ...a, volume: Number(e.target.value) })} />
            <span className="density-value">{pct(a.volume)}</span>
          </label>
          <label title="Pitch: higher is faster and squeakier">
            <span className="muted small">Pitch</span>
            <input type="range" min={0.5} max={3} step={0.05} value={a.pitch} onChange={(e) => onEvent(event.id, { ...a, pitch: Number(e.target.value) })} />
            <span className="density-value">{a.pitch.toFixed(2)}×</span>
          </label>
          <button className="btn small-btn" disabled={!custom} title="Back to the Default profile’s sound" onClick={() => onEvent(event.id, null)}>Reset</button>
        </div>
      )}
    </div>
  );
}

// ---------- picking a sound ----------

// Rows mount only while open; the library has thousands of sounds.
function SoundCategory({ category, count, forceOpen, children }: { category: string; count: number; forceOpen: boolean; children: () => ReactNode }) {
  const [open, setOpen] = useState(false);
  const isOpen = open || forceOpen;
  return (
    <details className="sound-category" open={isOpen} onToggle={(e) => !forceOpen && setOpen(e.currentTarget.open)}>
      <summary>
        {category} <span className="muted small">{count}</span>
      </summary>
      {isOpen && children()}
    </details>
  );
}

function SoundPicker({ lib, event, current, master, onPick, onClose }: { lib: SoundLibrary; event: SoundEventDef; current: SoundAssignment; master: number; onPick: (sound: string | null) => void; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [onlyOriginal, setOnlyOriginal] = useState(false);
  const q = query.trim().toLowerCase();
  const match = (x: LibrarySound) => (!onlyOriginal || !!x.original) && (!q || `${x.name} ${x.category} ${x.tags.join(' ')} ${lib.credits[x.credit]?.title ?? ''} ${lib.credits[x.credit]?.author ?? ''}`.toLowerCase().includes(q));
  const suggested = (lib.suggestions[event.id] ?? []).map((id) => lib.byId.get(id)).filter((x): x is LibrarySound => !!x && match(x));
  const categories = useMemo(() => {
    const m = new Map<string, LibrarySound[]>();
    for (const x of lib.sounds) {
      const list = m.get(x.category) ?? [];
      list.push(x);
      m.set(x.category, list);
    }
    return [...m];
  }, [lib]);
  const shown = categories.map(([c, list]) => [c, list.filter(match)] as const).filter(([, list]) => list.length);
  const total = shown.reduce((n, [, list]) => n + list.length, 0);

  const item = (x: LibrarySound, where: string) => {
    const credit = lib.credits[x.credit];
    return (
      <div key={`${where}:${x.id}`} className={`sound-item ${x.id === current.sound ? 'current' : ''}`} onClick={() => onPick(x.id)} title={credit ? creditLine(credit) : undefined}>
        <PreviewButton lib={lib} sound={x} k={`pick:${x.id}`} volume={current.volume * master} pitch={current.pitch} />
        <div className="sound-item-text">
          <SoundName sound={x} />
          <span className="muted small">
            {secs(x.duration)} · {credit ? `${credit.author} · ${credit.license}` : x.credit}
          </span>
        </div>
        {x.id === current.sound ? <Icon name="check" size={16} /> : <span className="sound-use">Use</span>}
      </div>
    );
  };

  return (
    <div className="sound-picker-back" onClick={onClose}>
      <div className="sound-picker" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2>Sound for “{event.label}”</h2>
          <button className="icon-btn" onClick={onClose}><Icon name="close" /></button>
        </div>
        <div className="sound-picker-tools">
          <input className="text search" autoFocus placeholder="Search names, packs, authors…" value={query} onChange={(e) => setQuery(e.target.value)} />
          <label className="sound-toggle"><input type="checkbox" className="check" checked={onlyOriginal} onChange={(e) => setOnlyOriginal(e.target.checked)} /> Originals only</label>
        </div>
        <div className="modal-body">
          <p className="muted small">Suggested sounds come first. You can pick any sound for any action. Previews play at this action’s volume and pitch.</p>
          {!q && !onlyOriginal && (
            <div className={`sound-item ${current.sound === null ? 'current' : ''}`} onClick={() => onPick(null)}>
              <span className="icon-btn sound-play"><Icon name="minus" size={16} /></span>
              <div className="sound-item-text"><b>Silent</b><span className="muted small">No sound for this action</span></div>
              {current.sound === null ? <Icon name="check" size={16} /> : <span className="sound-use">Use</span>}
            </div>
          )}
          {suggested.length > 0 && (
            <>
              <h3>Suggested for {event.label.toLowerCase()}</h3>
              {suggested.map((x) => item(x, 'suggested'))}
            </>
          )}
          <h3>All sounds ({total})</h3>
          {shown.map(([category, list]) => (
            <SoundCategory key={category} category={category} count={list.length} forceOpen={!!q || onlyOriginal}>
              {() => list.map((x) => item(x, category))}
            </SoundCategory>
          ))}
          {!total && <div className="muted">Nothing matches.</div>}
        </div>
      </div>
    </div>
  );
}

// ---------- credits ----------

function Credits({ lib }: { lib: SoundLibrary }) {
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const x of lib.sounds) m.set(x.credit, (m.get(x.credit) ?? 0) + 1);
    return m;
  }, [lib]);
  const entries = Object.entries(lib.credits).sort(([, a], [, b]) => a.license.localeCompare(b.license) || a.title.localeCompare(b.title));
  const needs = entries.filter(([, c]) => c.license !== 'CC0' && c.license !== 'Original');
  const row = ([key, c]: [string, SoundLibrary['credits'][string]]) => (
    <li key={key}>
      {c.url ? <a href={c.url} target="_blank" rel="noreferrer">{c.title}</a> : c.title} by {c.authorUrl ? <a href={c.authorUrl} target="_blank" rel="noreferrer">{c.author}</a> : c.author}
      <span className="muted small"> · {c.licenseUrl ? <a href={c.licenseUrl} target="_blank" rel="noreferrer">{c.license}</a> : c.license} · {c.source} · {counts.get(key) ?? 0} sounds</span>
    </li>
  );
  return (
    <div className="modal-body sound-credits">
      <p className="muted small">
        {lib.sounds.length} sounds from {entries.length} sources. Sounds marked Original are the game’s own. Everything else is CC0, CC-BY or OGA-BY: free to use, and the CC-BY and OGA-BY ones must be credited in a finished game.
      </p>
      <h3>Credit required ({needs.length})</h3>
      <ul>{needs.map(row)}</ul>
      <h3>No credit required ({entries.length - needs.length})</h3>
      <ul>{entries.filter(([, c]) => c.license === 'CC0' || c.license === 'Original').map(row)}</ul>
    </div>
  );
}
